/**
 * Concierge Cases admin tab (Feature 106).
 *
 * The Care Concierge team's queue: every tracked case whatever its origin — an advisor's paid
 * consultation, a request that came through the platform, or one logged here by hand. Deliberately
 * separate from the platform's care-seeker consultations, which are a different concept with
 * different parties.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useToast } from '@/shared/components/Toast';
import { CaseEditorDialog } from './CaseEditorDialog';
import { ManualCaseDialog } from './ManualCaseDialog';
import { SessionReturnRequests } from './SessionReturnRequests';
import {
  createManualCase,
  fetchCases,
  updateCase,
  type CaseRequestSource,
  type CaseStatus,
  type ConciergeCase,
  type CreateManualCasePayload,
  type UpdateCasePayload,
} from '../../services/careConciergeCasesApi';

const FILTERS: Array<{ value: CaseStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'AWAITING_CLIENT', label: 'Awaiting client' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'OCCURRED', label: 'Occurred' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'NOT_COMPLETED', label: 'Not completed' },
];

const STATUS_LABEL: Record<CaseStatus, string> = {
  AWAITING_CLIENT: 'Awaiting client',
  SCHEDULED: 'Scheduled',
  OCCURRED: 'Occurred',
  COMPLETED: 'Completed',
  NOT_COMPLETED: 'Not completed',
};

const SOURCE_LABEL: Record<CaseRequestSource, string> = {
  PAID_CONSULTATION: 'Paid consultation',
  SP_NOT_FOUND: 'SP not found',
  DIRECT_ADVISOR_REQUEST: 'Direct advisor request',
  DISCOVERY_CALL: 'Discovery call',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * follow_up_on is a DATE column — a calendar day, not an instant — and arrives as UTC midnight.
 * Formatting it in the reader's timezone would move it a day back west of UTC. The other dates
 * here are real timestamps, so they keep formatDate.
 */
const formatDateOnly = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
    ?.message ??
  (error as { message?: string })?.message ??
  fallback;

export const ConciergeCasesTab = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<CaseStatus | 'ALL'>('ALL');
  const [editing, setEditing] = useState<ConciergeCase | null>(null);
  const [isLogging, setIsLogging] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'care-concierge', 'cases', filter],
    queryFn: () => fetchCases(filter === 'ALL' ? undefined : filter),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'care-concierge'] });

  const save = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCasePayload }) =>
      updateCase(id, payload),
    onSuccess: () => {
      setEditing(null);
      invalidate();
      showToast('Case saved.', 'success');
    },
    onError: (error) => showToast(errorMessage(error, 'Could not save the case.'), 'error'),
  });

  const logManual = useMutation({
    mutationFn: (payload: CreateManualCasePayload) => createManualCase(payload),
    onSuccess: (created) => {
      setIsLogging(false);
      invalidate();
      // Feature 127: the case is saved even when the invitation it asked for was refused — the
      // warning says so, and why, instead of a success that hides it.
      if (created.inviteWarning) {
        showToast(created.inviteWarning, 'error');
      } else {
        showToast('Case logged.', 'success');
      }
    },
    onError: (error) => showToast(errorMessage(error, 'Could not log the case.'), 'error'),
  });

  return (
    <div data-testid="concierge-cases-tab" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Concierge Cases</h2>
          <p className="text-sm text-gray-600">
            Every tracked Care Concierge case, whichever way it arrived.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setIsLogging(true)}>
          Log new case
        </Button>
      </div>

      {/* Above the queue on purpose: an advisor waiting on a decision is the only thing here that
          is blocking someone else. */}
      <SessionReturnRequests />

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={filter === option.value ? 'default' : 'outline'}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading cases…</p>}
      {isError && <p className="text-sm text-destructive">Could not load cases.</p>}

      {data && data.length === 0 && (
        <p className="text-sm text-muted-foreground">No cases match this filter.</p>
      )}

      {data && data.length > 0 && (
        <Card className="divide-y divide-border">
          {data.map((conciergeCase) => (
            <div
              key={conciergeCase.id}
              className="flex flex-wrap items-start justify-between gap-3 p-4"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {conciergeCase.personName || conciergeCase.personEmail}
                  {conciergeCase.clientUserId && (
                    <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                      On bQuest
                    </span>
                  )}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {SOURCE_LABEL[conciergeCase.requestSource]}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {conciergeCase.advisorName ?? 'No advisor'}
                  {conciergeCase.firmName && ` · ${conciergeCase.firmName}`} ·{' '}
                  {STATUS_LABEL[conciergeCase.status]}
                  {conciergeCase.scheduledAt && ` · booked ${formatDate(conciergeCase.scheduledAt)}`}
                </p>

                {conciergeCase.referredProviders.length > 0 && (
                  <p className="mt-1 text-sm text-gray-600">
                    Referred {conciergeCase.referredProviders.map((p) => p.name).join(', ')}
                  </p>
                )}

                {conciergeCase.followUps.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Follow-ups: {conciergeCase.followUps.map((f) => formatDateOnly(f.followUpOn)).join(', ')}
                  </p>
                )}

                {/* Says which way the switch is set, because the consequence of it being on is
                    that the advisor is reading these notes. */}
                {conciergeCase.notes && (
                  <p className="mt-1 text-xs">
                    {conciergeCase.sharedWithAdvisor ? (
                      <span className="text-amber-700">Notes shared with the advisor</span>
                    ) : (
                      <span className="text-muted-foreground">Notes not shared</span>
                    )}
                  </p>
                )}
              </div>

              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEditing(conciergeCase)}
              >
                Edit case
              </Button>
            </div>
          ))}
        </Card>
      )}

      {editing && (
        <CaseEditorDialog
          conciergeCase={editing}
          isSaving={save.isPending}
          onSave={(payload) => save.mutate({ id: editing.id, payload })}
          // The care plan saves on its own, so the open dialog is refreshed in place rather
          // than closed — the team is usually mid-edit on the notes when they attach one.
          onCarePlanChanged={(hasCarePlan) => {
            invalidate();
            setEditing({ ...editing, hasCarePlan });
          }}
          onFollowUpsChanged={(followUps) => {
            invalidate();
            setEditing({ ...editing, followUps });
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {isLogging && (
        <ManualCaseDialog
          isSaving={logManual.isPending}
          onSave={(payload) => logManual.mutate(payload)}
          onClose={() => setIsLogging(false)}
        />
      )}
    </div>
  );
};
